import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

function SystemRequirementSwLinksModal({ show, onClose, onSaved, requirement }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [availableRequirements, setAvailableRequirements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !requirement) return;

    setSearch("");
    setDebouncedSearch("");
    setSelectedIds((requirement.SWRequirements ?? []).map((item) => Number(item.id)));
  }, [show, requirement]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!show) return;

    const controller = new AbortController();

    async function loadAvailableRequirements() {
      setLoading(true);
      try {
        const pageSize = 250;
        const loadedRows = [];
        let page = 1;
        let total = null;

        while (total === null || loadedRows.length < total) {
          const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
            hideHeadings: "true",
            sortField: "number",
            sortDir: "asc"
          });

          if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

          const res = await fetchWithAuth(`/api/requirements?${params.toString()}`, {
            signal: controller.signal
          });

          if (!res.ok) {
            setAvailableRequirements([]);
            return;
          }

          const data = await res.json();
          const rows = Array.isArray(data.rows) ? data.rows : [];
          total = Number.isInteger(data.total) ? data.total : loadedRows.length + rows.length;
          loadedRows.push(...rows);

          if (rows.length < pageSize) break;
          page += 1;
        }

        setAvailableRequirements(loadedRows);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("loadAvailableSWRequirements aborted:", err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    loadAvailableRequirements();

    return () => controller.abort();
  }, [show, debouncedSearch]);

  const selectedRequirementIds = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleRequirements = useMemo(() => {
    const byId = new Map();
    const existingLinkedRequirements = requirement?.SWRequirements ?? [];

    for (const item of existingLinkedRequirements) byId.set(Number(item.id), item);
    for (const item of availableRequirements) byId.set(Number(item.id), item);

    return [...byId.values()].sort((left, right) => {
      const numberCompare = String(left.number ?? "").localeCompare(
        String(right.number ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      if (numberCompare !== 0) return numberCompare;
      return Number(left.id) - Number(right.id);
    });
  }, [availableRequirements, requirement]);

  const toggleRequirement = (id) => {
    const numericId = Number(id);
    setSelectedIds((current) => (
      current.includes(numericId)
        ? current.filter((item) => item !== numericId)
        : [...current, numericId]
    ));
  };

  const saveLinks = async () => {
    if (!requirement) return;

    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/system-requirements/${requirement.id}/sw-requirements`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requirementIds: selectedIds })
      });

      if (!res.ok) {
        const error = await res.json().catch(() => null);
        alert(error?.error || "Linking SW requirements failed");
        return;
      }

      await onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!requirement) return null;

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Link SW Requirements</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-3">
          <div className="fw-semibold">{requirement.number}</div>
          <div className="text-muted small">{requirement.name}</div>
        </div>

        <div className="d-flex align-items-center gap-2 mb-3">
          <Form.Control
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search SW requirements..."
            style={{ maxWidth: "360px" }}
          />
          <span className="badge text-bg-secondary">
            Selected: {selectedIds.length}
          </span>
          {loading && <Spinner animation="border" size="sm" />}
        </div>

        <div style={{ maxHeight: "54vh", overflowY: "auto" }}>
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }} />
                <th style={{ width: "180px" }}>Number</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {visibleRequirements.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-4">
                    No SW requirements found.
                  </td>
                </tr>
              ) : visibleRequirements.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Form.Check
                      checked={selectedRequirementIds.has(Number(item.id))}
                      onChange={() => toggleRequirement(item.id)}
                      aria-label={`Link ${item.number}`}
                    />
                  </td>
                  <td className="fw-semibold">{item.number}</td>
                  <td>{item.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="success" onClick={saveLinks} disabled={saving}>
          {saving ? "Saving..." : "Save links"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SystemRequirementSwLinksModal;
